import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { BackgroundActivity, BackgroundActivityStatus } from '@models/backgroundActivities';

import { loadBackgroundActivities, saveBackgroundActivities } from '../localstorege_handler';

export interface BackgroundActivityStoreState {
  activities: Record<string, BackgroundActivity>;
  byTargetKey: Record<string, string>;
  upsertActivity: (activity: BackgroundActivity) => void;
  patchActivity: (id: string, patch: Partial<BackgroundActivity>) => void;
  removeActivity: (id: string) => void;
  hydrateActivities: () => void;
}

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const settled = (activities: Record<string, BackgroundActivity>) => {
  const cutoff = Date.now() - MAX_AGE_MS;
  return Object.fromEntries(
    Object.entries(activities).filter(
      ([, activity]) =>
        activity.status === BackgroundActivityStatus.COMPLETED && activity.startedAt >= cutoff
    )
  );
};

const indexByTargetKey = (activities: Record<string, BackgroundActivity>) =>
  Object.fromEntries(
    Object.values(activities).map((activity) => [activity.targetKey, activity.id])
  );

export const useBackgroundActivityStore = createWithEqualityFn<BackgroundActivityStoreState>(
  (set) => ({
    activities: {},
    byTargetKey: {},

    upsertActivity: (activity) => {
      set((state) => {
        const activities = { ...state.activities, [activity.id]: activity };
        saveBackgroundActivities(settled(activities));
        return {
          activities: activities,
          byTargetKey: { ...state.byTargetKey, [activity.targetKey]: activity.id }
        };
      });
    },

    patchActivity: (id, patch) => {
      set((state) => {
        const current = state.activities[id];
        if (!current) return state;
        const next = { ...current, ...patch };
        let nextByTargetKey = state.byTargetKey;
        if (patch.targetKey !== undefined && patch.targetKey !== current.targetKey) {
          nextByTargetKey = { ...state.byTargetKey };
          if (nextByTargetKey[current.targetKey] === id) {
            delete nextByTargetKey[current.targetKey];
          }
          nextByTargetKey[next.targetKey] = id;
        }
        const activities = { ...state.activities, [id]: next };
        saveBackgroundActivities(settled(activities));
        return { ...state, activities: activities, byTargetKey: nextByTargetKey };
      });
    },

    removeActivity: (id) => {
      set((state) => {
        const activity = state.activities[id];
        if (!activity) return state;
        const nextActivities = { ...state.activities };
        delete nextActivities[id];
        const nextByTargetKey = { ...state.byTargetKey };
        delete nextByTargetKey[activity.targetKey];
        saveBackgroundActivities(settled(nextActivities));
        return { ...state, activities: nextActivities, byTargetKey: nextByTargetKey };
      });
    },

    hydrateActivities: () => {
      const restored = Object.fromEntries(
        Object.entries(settled(loadBackgroundActivities())).map(([id, activity]) => [
          id,
          { ...activity, restored: true }
        ])
      );
      // Merge, never replace — an upload running right now lives only in memory.
      set((state) => {
        const liveTargetKeys = new Set(
          Object.values(state.activities).map((activity) => activity.targetKey)
        );
        const withoutCollisions = Object.fromEntries(
          Object.entries(restored).filter(([, activity]) => !liveTargetKeys.has(activity.targetKey))
        );
        const activities = { ...withoutCollisions, ...state.activities };
        saveBackgroundActivities(settled(activities));
        return { activities: activities, byTargetKey: indexByTargetKey(activities) };
      });
    }
  }),
  shallow
);

export function resetBackgroundActivityStore(): void {
  useBackgroundActivityStore.setState({ activities: {}, byTargetKey: {} });
}
